import type { Account, DashboardData, ExpenseInput, Trip } from "../../types";
import { inferCurrencyForDestination } from "../../lib/countries";
import { nowTimeIn, todayIn } from "../../lib/finance";
import { getDefaultTripAccount, getDefaultTripToAccount } from "../../lib/trips";

export function resolveDefaultTrip(
  trips: Trip[],
  timezone?: string,
  initialTripId?: string,
): Trip | undefined {
  if (initialTripId) {
    return trips.find((t) => t.id === initialTripId);
  }
  const today = todayIn(timezone);
  return trips.find(
    (t) =>
      t.status === "active" ||
      (t.startDate <= today && today <= t.endDate && t.status !== "completed" && t.status !== "archived"),
  );
}

export function createInitialExpenseInput({
  data,
  accounts,
  trips,
  preferred,
  initialInput,
  onReview,
}: {
  data: DashboardData;
  accounts: Account[];
  trips: Trip[];
  preferred?: Account;
  initialInput?: ExpenseInput;
  onReview?: unknown;
}): ExpenseInput {
  const defaultTrip = resolveDefaultTrip(trips, data.timezone, initialInput?.tripId);
  const activeTripId = initialInput?.tripId ?? (initialInput ? undefined : defaultTrip?.id);
  const activeTrip = trips.find((t) => t.id === activeTripId);
  const matchedDest =
    initialInput?.tripDestination ||
    (activeTrip && preferred ? activeTrip.destinations.find((d) => inferCurrencyForDestination(d) === preferred.currency) : undefined) ||
    (activeTrip?.destinations.length === 1 ? activeTrip.destinations[0] : undefined);

  let resolvedAccountId = initialInput?.accountId;
  if (!resolvedAccountId && activeTrip) {
    const defaultTripAcc = getDefaultTripAccount(activeTrip, accounts, matchedDest, trips, initialInput?.type);
    resolvedAccountId = defaultTripAcc?.id;
  }
  if (!resolvedAccountId) {
    resolvedAccountId = preferred?.id || "";
  }

  let resolvedToAccountId = initialInput?.toAccountId;
  if (!resolvedToAccountId && initialInput?.type === "Transfer" && activeTrip) {
    const defaultTripToAcc = getDefaultTripToAccount(activeTrip, accounts, resolvedAccountId, matchedDest, trips);
    resolvedToAccountId = defaultTripToAcc?.id;
  }

  const defaultSplitType = (() => {
    try {
      const s = typeof localStorage !== "undefined" ? localStorage.getItem("barnyard-default-split-type") : null;
      if (s === "equal" || s === "personal" || s === "joint") return s;
    } catch {}
    return "joint";
  })();

  const hasCustomReportable =
    initialInput?.reportableAmount !== undefined &&
    initialInput.reportableAmount !== "" &&
    Number(initialInput.reportableAmount) !== Number(initialInput.amount);

  return initialInput
    ? {
        ...(!onReview ? { time: nowTimeIn(data.timezone) } : {}),
        tripId: activeTripId,
        tripDestination: matchedDest,
        split: initialInput.split || { type: defaultSplitType },
        ...(initialInput.isSettlement ? { isSettlement: true } : {}),
        ...(resolvedToAccountId ? { toAccountId: resolvedToAccountId } : {}),
        ...initialInput,
        reportableAmount: hasCustomReportable ? initialInput.reportableAmount : undefined,
        accountId: initialInput.accountId || resolvedAccountId,
      }
    : {
        requestId: crypto.randomUUID(),
        accountId: resolvedAccountId,
        ...(resolvedToAccountId ? { toAccountId: resolvedToAccountId } : {}),
        amount: "",
        date: todayIn(data.timezone),
        time: nowTimeIn(data.timezone),
        category: "Uncategorized",
        subcategory: "",
        title: "",
        notes: "",
        tripId: activeTripId,
        tripDestination: matchedDest,
        split: { type: defaultSplitType },
      };
}
